import React, { useEffect, useState } from "react";

const links = [
  { id: "hero", label: "Home" },
  { id: "about", label: "About" },
  { id: "services", label: "Services" },
  { id: "results", label: "Results" },
  { id: "voices", label: "Voices" },
  { id: "visit", label: "Visit" },
];

export default function Nav({ onBook }) {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollTo = (id) => {
    setOpen(false);
    const el = document.getElementById(id);
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <header
      data-testid="site-nav"
      role="banner"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? "py-2" : "py-4 md:py-6"
      }`}
    >
      {/* WCAG 2.4.1: Skip navigation */}
      <a href="#main-content" className="skip-nav">Skip to main content</a>
      <div className="container mx-auto px-5 md:px-10">
        <nav
          className={`flex items-center justify-between rounded-full transition-all duration-500 ${
            scrolled ? "glass px-4 py-2 shadow-[0_10px_40px_-20px_rgba(56,79,49,0.25)]" : "px-2 py-1.5"
          }`}
        >
          <button onClick={() => scrollTo("hero")} data-testid="nav-logo" className="flex items-center gap-3 group">
            <img
              src="/img/logo.png"
              alt="Dentist"
              className={`transition-all duration-500 ${scrolled ? "h-16" : "h-20 md:h-24"}`}
            />
            <span className="hidden md:flex flex-col leading-tight">
              <span className="font-display text-sage-700 text-[17px] tracking-wide">Dentist</span>
              <span className="text-[10px] uppercase tracking-[0.28em] text-sage-600/80">Premium Dental Care</span>
            </span>
          </button>

          <ul className="hidden lg:flex items-center gap-9 text-[13px] uppercase tracking-[0.22em] text-ink/80">
            {links.map((l) => (
              <li key={l.id}>
                <button
                  data-testid={`nav-link-${l.id}`}
                  onClick={() => scrollTo(l.id)}
                  className="underline-grow text-ink/70 hover:text-sage-700 transition-colors"
                >
                  {l.label}
                </button>
              </li>
            ))}
          </ul>

          <div className="flex items-center gap-2">
            <button
              data-testid="nav-book-btn"
              onClick={onBook}
              className="hidden md:inline-flex items-center gap-2 rounded-full bg-sage-700 text-cream px-5 py-2.5 text-[12px] tracking-[0.22em] uppercase btn-glow"
            >
              Book
              <span className="inline-block w-2 h-2 rounded-full bg-gold-300 shadow-[0_0_12px_3px_rgba(201,169,110,0.7)]" />
            </button>
            <button
              data-testid="nav-burger"
              onClick={() => setOpen((v) => !v)}
              className={`lg:hidden burger px-3 py-2 text-sage-800 ${open ? "open" : ""}`}
              aria-label="Toggle menu"
            >
              <span /> <span /> <span />
            </button>
          </div>
        </nav>

        {/* Mobile menu */}
        <div
          className={`lg:hidden overflow-hidden transition-all duration-500 ${
            open ? "max-h-[420px] mt-3 opacity-100" : "max-h-0 opacity-0"
          }`}
        >
          <div className="glass rounded-3xl p-6 space-y-4">
            {links.map((l) => (
              <button
                key={l.id}
                onClick={() => scrollTo(l.id)}
                data-testid={`mobile-link-${l.id}`}
                className="block w-full text-left font-display text-2xl text-sage-800 hover:text-gold-600 transition-colors"
              >
                {l.label}
              </button>
            ))}
            <button
              onClick={() => { setOpen(false); onBook(); }}
              data-testid="mobile-book-btn"
              className="mt-2 w-full rounded-full bg-sage-700 text-cream py-3 uppercase tracking-[0.22em] text-xs"
            >
              Book a Consultation
            </button>
          </div>
        </div>
      </div>
    </header>
  );
}
